import React from 'react';
import classnames from 'classnames';
import { Field } from 'hellospa/components/editor/types';
import {
  Origin, signatureRequestContext, zoomContext, ZoomContextShape,
} from 'hellospa/components/signature-request/context';
import { ORIGIN_PAGE } from './constants';
import { positionContext, PositionContextShape } from './position-context';
import DisplayField, { FieldContextProvider, WrapperStyles } from './display-field';
import styles from './handle.scss';

type Props = {
  // The way I built this is super awkward. Signer and Overlay use DisplayField,
  // but the Editor uses a different component. I don't see a good way to make
  // the types cooperate here.
  Field: React.ComponentType<any>,
  fieldData: Field,
  origin?: Origin,
}

type ContextData = {
  isSelected: boolean,
  textScale: ZoomContextShape['textScale'],
  toScreenCoords: PositionContextShape['toScreenCoords']
  documentPreview: boolean,
  inlinePreviewMode: boolean,
  lastUpdatedFieldId?: Field['id'],
}

function Handle({
  Field = DisplayField,
  fieldData,
  origin = ORIGIN_PAGE,
  isSelected,
  textScale,
  toScreenCoords,
  documentPreview,
  inlinePreviewMode,
  lastUpdatedFieldId,
}: Props & ContextData) {
  const { x, y, width, height } = React.useMemo(
    () => toScreenCoords(fieldData, origin),
    [fieldData, origin, toScreenCoords],
  );

  const elementRef = React.useRef(null);
  // If this is a preview and we have a lastUpdatedFieldId,
  // scroll to it, because it must have just been updated
  if (inlinePreviewMode && lastUpdatedFieldId === fieldData.id) {
    // @ts-ignore
    if (elementRef && elementRef.current && elementRef.current.scrollIntoView) {
      // For some reason when I add behavior: smooth it is not working
      // @ts-ignore
      elementRef.current.scrollIntoView();
    }
  }

  const handleStyle = {
    left: x,
    top: y,
    width,
    height,
  };

  const wrapperStyles = React.useMemo((): WrapperStyles => ({
    position: 'absolute',
    height: `${100 / textScale}%`,
    width: `${100 / textScale}%`,
    transformOrigin: 'top left',
    transform: `scale(${textScale})`,
    // Safari requires the WebKit prefix
    WebkitTransformOrigin: 'top left',
    WebkitTransform: `scale(${textScale})`,
  }), [textScale]);

  return (
    <div
      ref={elementRef}
      data-field={fieldData.name || fieldData.id}
      data-qa-ref={`field-${fieldData.type}`}
      className={classnames(
        styles.handle,
        styles[`signer-${fieldData.signer}`],
        {
          [styles.selected]: isSelected,
          [styles.documentPreview]: documentPreview,
          [styles.readOnly]: fieldData.readOnly,
        },
      )}
      style={handleStyle}
      data-field-data={process.env.NODE_ENV !== 'test' ? undefined : JSON.stringify(fieldData)}
    >
      <FieldContextProvider fieldData={fieldData} wrapperStyles={wrapperStyles}>
        <Field
          fieldData={fieldData}
          textScale={textScale}
          disabled={documentPreview && fieldData.signer === 'preparer'}
          height={wrapperStyles.height}
          width={wrapperStyles.width} />
      </FieldContextProvider>
    </div>
  );
}

const MemoHandle = React.memo(Handle);

export default function HandleContext(props: Props) {
  const {
    selectedFieldIds,
    documentPreview,
    inlinePreviewMode,
    lastUpdatedFieldId,
  } = React.useContext(signatureRequestContext);
  const { textScale } = React.useContext(zoomContext);
  const { toScreenCoords } = React.useContext(positionContext);

  return (
    <MemoHandle
      {...props}
      lastUpdatedFieldId={lastUpdatedFieldId}
      isSelected={selectedFieldIds.includes(props.fieldData.id)}
      textScale={textScale}
      documentPreview={documentPreview}
      inlinePreviewMode={inlinePreviewMode}
      toScreenCoords={toScreenCoords} />
  );
}
